import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { v4 } from 'uuid';
import classNames from 'classnames';
import { isEqual } from 'lodash';
import Modal from 'components/shared/Modal/Modal';
import ProvisionTable from './ProvisionTable/ProvisionTable';
import WarningBox from 'components/shared/WarningBox/WarningBox';
import { ProgressIndicator } from 'components/shared/Progress';
import LoadingIndicator from 'components/shared/LoadingIndicator/LoadingIndicator';
import ActionDialog from 'components/shared/ActionDialog/ActionDialog';
import CommissionErrorBox from '../Commissioning/CommissionErrorBox/CommissionErrorBox';
import { createApiPolling } from 'common/utils/helpers';
import { ERROR_CODES, PERMISSION_ERROR_MESSAGE } from 'common/utils/constants';
import { formatTableData, hasSpecialCharactersAndSpaces } from './utils';
import { addToastNotification, getSingleDevice, updatePivotOverviewPageSingleSelectedDevice } from 'redux/actions';
import { addProvisionSu } from 'apis/provisionSu/addProvisionSu';
import { deleteProvisionSu } from 'apis/provisionSu/deleteProvisionSu';
import { getProvisionSuActionStatus } from 'apis/provisionSu/getProvisionSuActionStatus';
import styles from './ProvisionModal.module.css';
import { hasGa2ProvisionPermission } from '../MaintenancePanelBar/utils';

const SU_REMOVAL_WARNING_MESSAGE = 'After removal, the Sector will remain empty until a serial number is assigned.';

const WARNING_MESSAGE =
  'Please refer to Device Manifest to assign SU serial numbers to the Sectors. Incorrect assignments may degrade Sector 5G performance.';

const ACTION_PENDING_MESSAGE = 'This action may take up to 2 minutes.';

const INPUT_ERROR_MESSAGE = 'Input fields cannot contain any special characters or spaces.';

const SECTOR_ERROR_MESSAGE = 'Please ensure all Sectors have both a valid device ID and a valid NE-type';

const DEFAULT_ERROR_STATE = { code: null, message: null };

const ProvisionModal = ({ onClose, targetDevice }) => {
  const permissions = useSelector((store) => store.data?.rolePermissions?.permissions);
  const dispatch = useDispatch();
  const [isDeleteSuModalOpen, setIsDeleteSuModalOpen] = useState(false);
  const [isAddSuModalOpen, setIsAddSuModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState(DEFAULT_ERROR_STATE);
  const [addError, setAddError] = useState(DEFAULT_ERROR_STATE);
  const [pollStatusError, setPollStatusError] = useState(DEFAULT_ERROR_STATE);
  const [hasInputError, setHasInputError] = useState(false);
  const deletingSuId = useRef(null);

  const canProvisionGa2 = hasGa2ProvisionPermission(permissions);

  const defaultValues = useMemo(
    () => ({
      name: '',
      state: {
        data: formatTableData(targetDevice)
      }
    }),
    [targetDevice]
  );

  const form = useForm({ defaultValues });
  const { handleSubmit, control, setValue } = form;
  const data = useWatch({ name: 'state.data', control });

  useEffect(() => {
    if (data?.length) {
      const hasInvalidInput = data?.some((item) => hasSpecialCharactersAndSpaces(item.device) === true);
      setHasInputError(hasInvalidInput);
    }
  }, [data]);

  const updateSingleDevice = useCallback(
    (deviceId) => {
      dispatch(
        getSingleDevice(deviceId, (res) => {
          if (res?.deviceId) {
            setValue('state.data', formatTableData(res));
            dispatch(updatePivotOverviewPageSingleSelectedDevice(res));
          }
        })
      );
    },
    [dispatch, setValue]
  );

  const renderNotification = useCallback(() => {
    dispatch(
      addToastNotification({
        id: v4(),
        title: `SU${deletingSuId.current}`,
        description: `has been removed from the Sector.`
      })
    );
  }, [dispatch, deletingSuId]);

  const disableSaveAction = useMemo(() => {
    if (!canProvisionGa2) return { disabled: true, message: PERMISSION_ERROR_MESSAGE };
    if (isEqual(defaultValues.state.data, data) || submitting || hasInputError)
      return { disabled: true, message: SECTOR_ERROR_MESSAGE };

    if (data.some((item) => (item.device && item.neType === '') || (item.device === '' && item.neType)))
      return { disabled: true, message: SECTOR_ERROR_MESSAGE };

    return { disabled: false, message: 'Save' };
  }, [data, defaultValues, submitting, hasInputError, canProvisionGa2]);

  const pollProvisionSuStatus = useCallback(
    async (uri) => {
      const checkCallback = (result) => {
        if (result?.runtimeStatus === 'Completed') {
          const actionType = result?.input?.method;
          const { message, status } = result?.customStatus;
          if (status === 'error') {
            if (actionType === 'ProvisionAddSU') setAddError({ code: status, message });
            if (actionType === 'ProvisionRemoveSU') {
              setDeleteError({ code: status, message });
              setIsDeleteSuModalOpen(false);
            }
          } else {
            actionType === 'ProvisionRemoveSU' && renderNotification();
            setIsDeleteSuModalOpen(false);
          }

          updateSingleDevice(targetDevice?.id);
          return true;
        }
        return false;
      };
      const poll = createApiPolling(getProvisionSuActionStatus, checkCallback, { delay: 3000 });

      try {
        await poll(uri);
        console.log('Stopped polling provisionSu action status.');
      } catch (error) {
        setSubmitting(false);
        // Commented this code as we dont require two popups.
        // setPollStatusError({ code: error.message, message: ERROR_CODES[`${error.message}`] });
      }
    },
    [targetDevice, updateSingleDevice, renderNotification]
  );

  const onSubmit = useCallback(
    async (values) => {
      if (disableSaveAction.disabled) return;
      setSubmitting(true);
      setIsAddSuModalOpen(true);
      try {
        const data = values?.state?.data;

        for (let item of data) {
          if (item.device === '' || item.neType === '') continue;
          if (isEqual(defaultValues.state.data[item.id - 1], item)) continue;

          const body = { 'su-id': item.id, 'serial-id': item.device, 'ne-type': item.neType };
          const result = await addProvisionSu(targetDevice?.id, body);
          const statusUri = result?.statusQueryGetUri;
          await pollProvisionSuStatus(statusUri);
        }
        setSubmitting(false);
      } catch (error) {
        setAddError({ code: error.message, message: ERROR_CODES[`${error.message}`] });
        setSubmitting(false);
        setIsAddSuModalOpen(false);
      }
    },
    [targetDevice, pollProvisionSuStatus, defaultValues, disableSaveAction]
  );

  const onDelete = useCallback(
    async (suId) => {
      setSubmitting(true);
      try {
        const donorId = targetDevice?.id;
        const result = await deleteProvisionSu(donorId, suId);
        const statusUri = result?.statusQueryGetUri;
        await pollProvisionSuStatus(statusUri);
        setSubmitting(false);
      } catch (error) {
        setDeleteError({ code: error.message, message: ERROR_CODES[`${error.message}`] });
        setSubmitting(false);
      }
    },
    [targetDevice, pollProvisionSuStatus]
  );

  const ApplySuDeletion = () => {
    onDelete(deletingSuId.current);
  };
  
  let isErrorMessageCalled = false;
  let errorMessage;

  const getFriendlyErrorMessage = (errorCode, errorType) => {
    // If the function has already been called, return early
    if (isErrorMessageCalled) return errorMessage;
    
    if (!errorCode) return 'An unknown error occurred. Please try again.';
 
     try {
    // Check if errorCode is a JSON string and parse it
    if (typeof errorCode === 'string' && errorCode.trim().startsWith('{')) {
      const errorObj = JSON.parse(errorCode);
      errorMessage = errorObj.errorMsg || 'An unknown error occurred.';
    } else {
      // Handle cases where the errorCode is a string that includes JSON
      const match = errorCode.match(/{.*"errorMsg":\s*"([^"]+)"/);
      if (match && match[1]) {
        errorMessage = match[1];
      } else {
        errorMessage = errorCode.split(':').pop().trim();
      }
    }
  } catch (e) {
    return 'An unknown error occurred.';
  }
    switch (errorType) {
      case 'pollstatus':
      case 'addError':
      case 'deleteError':
        isErrorMessageCalled = true;
        return errorMessage;
      default:
        isErrorMessageCalled = true;
        return 'An unknown error occurred. Please try again.';
    }
  };  

  return (
    <Modal
      role="dialog"
      aria-labelledby="provision_modal_title"
      className={styles.modal}
      close={onClose}
      animate={false}
      animateX={false}
      opaque={true}
      height={550}
      width={884}
      noPadding={true}
      overflow={true}
    >
      {isDeleteSuModalOpen && (
        <ActionDialog
          hideCloseButton={submitting}
          onClose={() => setIsDeleteSuModalOpen(false)}
          title={
            submitting ? (
              <LoadingIndicator text={`Removing SU${deletingSuId.current} from the Sector`} />
            ) : (
              `Remove SU${deletingSuId.current} from the Sector?`
            )
          }
          message={submitting ? ACTION_PENDING_MESSAGE : SU_REMOVAL_WARNING_MESSAGE}
          fetching={submitting}
          actionHandler={!submitting && ApplySuDeletion}
        />
      )}
      {isAddSuModalOpen && (
        <ActionDialog
          hideCloseButton={submitting}
          onClose={() => {
            setIsAddSuModalOpen(false);
            onClose();
          }}
          title={submitting ? <LoadingIndicator text={`Adding SU(s) to the Sector(s)`} /> : 'Action Completed'}
          message={submitting ? ACTION_PENDING_MESSAGE : 'Added SU(s) to the Sector(s).'}
          fetching={submitting}
        />
      )}
      {deleteError.code && (
        <CommissionErrorBox
          title={`Remove Provisioned SU Error:`}
          message={getFriendlyErrorMessage(deleteError.message, 'deleteError')}
          onClose={() => {
            setIsDeleteSuModalOpen(false);
            setDeleteError(DEFAULT_ERROR_STATE);
          }}
        />
      )}
      {addError.code && (
        <CommissionErrorBox
          title={`Add Provision SU Error:`}
          message={getFriendlyErrorMessage(addError.message, 'addError')}
          onClose={() => {
            setIsAddSuModalOpen(false);
            setAddError(DEFAULT_ERROR_STATE);
          }}
        />
      )}
      {pollStatusError.code && (
        <CommissionErrorBox
          title={`Poll Provision Action Status Error:`}
          message={getFriendlyErrorMessage(pollStatusError.message, 'pollstatus')}
          onClose={() => {
            setIsAddSuModalOpen(false);
            setIsDeleteSuModalOpen(false);
            setPollStatusError(DEFAULT_ERROR_STATE);
          }}
        />
      )}
      {submitting && <ProgressIndicator />}
      <FormProvider {...form}>
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.root}>
            <div id="provision_modal_title" className={styles.header}>
              Sector Information
            </div>
            <div className={styles.banner}>
              <WarningBox warningMessages={WARNING_MESSAGE} />
            </div>
            <div className={styles.content}>
              <ProvisionTable
                data={data}
                defaultValues={defaultValues}
                deletingSuId={deletingSuId}
                setIsDeleteSuModalOpen={setIsDeleteSuModalOpen}
                setValue={setValue}
              />
            </div>

            <div className={styles.inputError}>{hasInputError ? INPUT_ERROR_MESSAGE : ''}</div>

            <div className={styles.actions}>
              <button type="button" className={styles.cancelButton} onClick={onClose}>
                Cancel
              </button>

              <button
                type="submit"
                className={classNames(styles.saveButton, disableSaveAction.disabled && styles.saveButtonDisabled)}
                data-tip={disableSaveAction.message}
              >
                Save
              </button>
            </div>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default ProvisionModal;
